/** @jsxImportSource @emotion/react */
import React from "react";
import {
    TimelineContainer,
    TimelineItem,
    TimelineHeader,
    TimelineContact,
    TimelineDate,
    TimelineGA,
    TimelineGrid,
    TimelineStat,
    TimelineStatLabel,
    TimelineStatValue,
    TrendBadge,
    EmptyState,
    EmptyIcon,
    EmptyTitle,
    EmptyDescription,
    DetailCard,
    DetailHeader,
    DetailTitle,
    DetailBody,
    DetailSection,
    DetailSectionTitle,
    DetailGrid,
    DetailItem,
    DetailItemLabel,
    DetailItemValue,
    ClinicalNotesBox,
    ClinicalNotesLabel,
    ClinicalNotesText,
    SecondaryButton,
    SeenByBadge,
    AlertCard,
    AlertIcon,
    AlertContent,
    AlertTitle,
    AlertDetail,
    AlertsContainer,
    SkeletonLine,
} from "./ANCClinicalAssessmentStyles";

/**
 * ANCContactTimeline — Enhanced chronological view of all ANC contacts
 *
 * Shows:
 *  - List of all recorded contacts with key vitals at a glance
 *  - Trend indicators (weight change, BP change between contacts)
 *  - Click to view full contact details
 *  - Danger flags for abnormal values
 *  - Empty state when no contacts exist
 */
const ANCContactTimeline = function(props) {
    const contacts = props.contacts;
    const loadingContacts = props.loadingContacts;
    const bookingData = props.bookingData;
    const selectedContact = props.selectedContact;
    const viewContact = props.viewContact;
    const closeContactDetail = props.closeContactDetail;

    /* ─── Loading state ─── */
    if (loadingContacts) {
        return (
            <div style={{ padding: "16px 0" }}>
                {[1, 2, 3].map(function(i) {
                    return (
                        <div
                            key={i}
                            style={{
                                background: "#fff",
                                border: "1px solid #e4e5e9",
                                borderRadius: "10px",
                                padding: "18px",
                                marginBottom: "10px",
                            }}
                        >
                            <div style={{ display: "flex", gap: "12px", marginBottom: "12px" }}>
                                <SkeletonLine width="90px" height="18px" />
                                <SkeletonLine width="110px" height="14px" />
                                <div style={{ marginLeft: "auto" }}>
                                    <SkeletonLine width="70px" height="14px" />
                                </div>
                            </div>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(4,1fr)",
                                    gap: "10px",
                                }}
                            >
                                {[1, 2, 3, 4].map(function(j) {
                                    return (
                                        <div key={j}>
                                            <SkeletonLine width="40px" height="10px" />
                                            <div style={{ height: "5px" }} />
                                            <SkeletonLine width="65px" height="16px" />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    /* ─── Detail view for a selected contact ─── */
    if (selectedContact) {
        return (
            <ContactDetailView
                contact={selectedContact}
                bookingData={bookingData}
                contacts={contacts}
                onClose={closeContactDetail}
            />
        );
    }

    /* ─── Empty state ─── */
    if (!contacts || contacts.length === 0) {
        return (
            <EmptyState>
                <EmptyIcon>📋</EmptyIcon>
                <EmptyTitle>No ANC Contacts Recorded</EmptyTitle>
                <EmptyDescription>
                    No clinical assessments have been recorded for this pregnancy yet. Switch
                    to the "Record Contact" tab to capture the first ANC clinical assessment.
                </EmptyDescription>
            </EmptyState>
        );
    }

    /* ─── Helper: check for danger values ─── */
    const isDangerBP = function(sys, dia) {
        return parseFloat(sys) >= 140 || parseFloat(dia) >= 90;
    };
    const isDangerFHR = function(fhr) {
        return fhr && (parseFloat(fhr) < 110 || parseFloat(fhr) > 160);
    };
    const isDangerHb = function(hb) {
        return hb && parseFloat(hb) < 11;
    };

    /* ─── Trend helpers ─── */
    const getWeightTrend = function(contact, idx) {
        const nextIdx = idx + 1;
        if (nextIdx >= contacts.length) return null;
        const prev = parseFloat(contacts[nextIdx].weight_kg);
        const cur = parseFloat(contact.weight_kg);
        if (!prev || !cur) return null;
        const diff = cur - prev;
        return {
            direction: diff > 0 ? "up" : diff < 0 ? "down" : "stable",
            label: diff > 0 ? "+" + diff.toFixed(1) : diff.toFixed(1),
        };
    };

    /* ─── Summary stats ─── */
    const totalContacts = contacts.length;
    const alertContacts = contacts.filter(function(c) {
        return isDangerBP(c.blood_pressure_systolic, c.blood_pressure_diastolic) ||
               isDangerFHR(c.fetal_heart_rate) ||
               isDangerHb(c.haemoglobin);
    }).length;

    /* ─── Timeline list ─── */
    return (
        <div>
            {/* Summary strip */}
            <div
                style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "16px",
                    flexWrap: "wrap",
                }}
            >
                <div
                    style={{
                        padding: "8px 16px",
                        background: "#ebf0f9",
                        borderRadius: "8px",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "#3871B7",
                    }}
                >
                    📊 {totalContacts} Contact{totalContacts !== 1 ? "s" : ""} Recorded
                </div>
                {alertContacts > 0 ? (
                    <div
                        style={{
                            padding: "8px 16px",
                            background: "#ffebee",
                            borderRadius: "8px",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#c62828",
                        }}
                    >
                        ⚠ {alertContacts} with alerts
                    </div>
                ) : null}
                <div
                    style={{
                        padding: "8px 16px",
                        background: "#e8f5e9",
                        borderRadius: "8px",
                        fontSize: "0.82rem",
                        fontWeight: 600,
                        color: "#2e7d32",
                    }}
                >
                    {8 - totalContacts} remaining
                </div>
            </div>

            <TimelineContainer>
                {contacts.map(function(contact, idx) {
                    const hasDanger =
                        isDangerBP(contact.blood_pressure_systolic, contact.blood_pressure_diastolic) ||
                        isDangerFHR(contact.fetal_heart_rate) ||
                        isDangerHb(contact.haemoglobin);
                    const wTrend = getWeightTrend(contact, idx);

                    return (
                        <TimelineItem
                            key={contact.id || idx}
                            active={idx === 0}
                            onClick={function() { viewContact(contact); }}
                        >
                            <TimelineHeader>
                                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                                    <TimelineContact>
                                        Contact #{contact.contact_number || idx + 1}
                                    </TimelineContact>
                                    <TimelineDate>
                                        {contact.contact_date
                                            ? new Date(contact.contact_date).toLocaleDateString(
                                                  "en-GB",
                                                  {
                                                      day: "numeric",
                                                      month: "short",
                                                      year: "numeric",
                                                  }
                                              )
                                            : "—"}
                                    </TimelineDate>
                                    {hasDanger ? (
                                        <span
                                            style={{
                                                fontSize: "0.68rem",
                                                fontWeight: 600,
                                                color: "#c53030",
                                                background: "#fed7d7",
                                                padding: "3px 10px",
                                                borderRadius: "12px",
                                            }}
                                        >
                                            ⚠ Alert
                                        </span>
                                    ) : null}
                                </div>
                                {contact.gestational_age_weeks ? (
                                    <TimelineGA>
                                        {contact.gestational_age_weeks} weeks
                                    </TimelineGA>
                                ) : null}
                            </TimelineHeader>

                            <TimelineGrid>
                                <TimelineStat>
                                    <TimelineStatLabel>BP</TimelineStatLabel>
                                    <TimelineStatValue
                                        danger={isDangerBP(
                                            contact.blood_pressure_systolic,
                                            contact.blood_pressure_diastolic
                                        )}
                                    >
                                        {contact.blood_pressure_systolic || "—"}/
                                        {contact.blood_pressure_diastolic || "—"}
                                    </TimelineStatValue>
                                </TimelineStat>
                                <TimelineStat>
                                    <TimelineStatLabel>Weight</TimelineStatLabel>
                                    <TimelineStatValue>
                                        {contact.weight_kg ? contact.weight_kg + " kg" : "—"}
                                        {wTrend ? (
                                            <TrendBadge direction={wTrend.direction}>
                                                {wTrend.direction === "up" ? "↑" : wTrend.direction === "down" ? "↓" : "→"}
                                                {wTrend.label}
                                            </TrendBadge>
                                        ) : null}
                                    </TimelineStatValue>
                                </TimelineStat>
                                <TimelineStat>
                                    <TimelineStatLabel>FHR</TimelineStatLabel>
                                    <TimelineStatValue
                                        danger={isDangerFHR(contact.fetal_heart_rate)}
                                    >
                                        {contact.fetal_heart_rate
                                            ? contact.fetal_heart_rate + " bpm"
                                            : "—"}
                                    </TimelineStatValue>
                                </TimelineStat>
                                <TimelineStat>
                                    <TimelineStatLabel>Hb</TimelineStatLabel>
                                    <TimelineStatValue
                                        danger={isDangerHb(contact.haemoglobin)}
                                    >
                                        {contact.haemoglobin
                                            ? contact.haemoglobin + " g/dL"
                                            : "—"}
                                    </TimelineStatValue>
                                </TimelineStat>
                            </TimelineGrid>

                            {/* MUAC mini indicator */}
                            {contact.muac_color_code ? (
                                <div style={{ marginTop: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span
                                        style={{
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            background:
                                                contact.muac_color_code === "green" ? "#2e7d32" :
                                                contact.muac_color_code === "yellow" ? "#f57f17" : "#c62828",
                                        }}
                                    />
                                    <span style={{ fontSize: "0.68rem", color: "#6f7077" }}>
                                        MUAC: {contact.muac_cm} cm
                                    </span>
                                </div>
                            ) : null}
                        </TimelineItem>
                    );
                })}
            </TimelineContainer>
        </div>
    );
};

/**
 * ContactDetailView — Enhanced full detail view of a single ANC contact
 */
const ContactDetailView = function(props) {
    const contact = props.contact;
    const bookingData = props.bookingData;
    const contacts = props.contacts || [];
    const onClose = props.onClose;

    /* ─── Build danger alerts for this contact ─── */
    const alerts = [];
    const sys = parseFloat(contact.blood_pressure_systolic);
    const dia = parseFloat(contact.blood_pressure_diastolic);
    const hb = parseFloat(contact.haemoglobin);
    const fhr = parseFloat(contact.fetal_heart_rate);
    const temp = parseFloat(contact.temperature);

    if (sys >= 140 || dia >= 90) {
        alerts.push({
            type: sys >= 160 || dia >= 110 ? "critical" : "warning",
            icon: "⚠️",
            title: sys >= 160 || dia >= 110 ? "Severe Hypertension" : "Elevated BP",
            detail: sys + "/" + dia + " mmHg",
        });
    }
    if (hb && hb < 11) {
        alerts.push({
            type: hb < 7 ? "critical" : "warning",
            icon: "⚠️",
            title: hb < 7 ? "Severe Anaemia" : "Anaemia",
            detail: "Hb " + hb + " g/dL",
        });
    }
    if (fhr && (fhr < 110 || fhr > 160)) {
        alerts.push({
            type: "critical",
            icon: "💗",
            title: "Abnormal FHR",
            detail: fhr + " bpm",
        });
    }
    if (temp && temp >= 37.5) {
        alerts.push({
            type: temp >= 38.5 ? "critical" : "warning",
            icon: "🌡️",
            title: "Fever",
            detail: temp + "°C",
        });
    }

    /* ─── Find index and prev contact for comparison ─── */
    const contactIdx = contacts.findIndex(function(c) { return c.id === contact.id; });
    const prevContact = contactIdx >= 0 && contactIdx < contacts.length - 1 ? contacts[contactIdx + 1] : null;

    const getCompare = function(current, previous, unit, higherIsBad) {
        if (!current || !previous) return null;
        const cur = parseFloat(current);
        const prev = parseFloat(previous);
        if (isNaN(cur) || isNaN(prev)) return null;
        const diff = cur - prev;
        if (diff === 0) return null;
        return {
            label: (diff > 0 ? "+" : "") + diff.toFixed(1) + " " + unit,
            color: higherIsBad
                ? (diff > 0 ? "#c62828" : "#2e7d32")
                : (diff > 0 ? "#2e7d32" : "#e65100"),
        };
    };

    return (
        <DetailCard>
            <DetailHeader>
                <div>
                    <DetailTitle>
                        Contact #{contact.contact_number || "—"} —{" "}
                        {contact.contact_date
                            ? new Date(contact.contact_date).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric",
                              })
                            : "—"}
                    </DetailTitle>
                    {contact.gestational_age_weeks ? (
                        <span style={{ fontSize: "0.78rem", color: "#3871B7", fontWeight: 600 }}>
                            Gestational Age: {contact.gestational_age_weeks} weeks
                        </span>
                    ) : null}
                </div>
                <SecondaryButton onClick={onClose}>
                    ← Back to Timeline
                </SecondaryButton>
            </DetailHeader>

            <DetailBody>
                {/* Alerts */}
                {alerts.length > 0 ? (
                    <AlertsContainer style={{ marginBottom: "20px" }}>
                        {alerts.map(function(a, i) {
                            return (
                                <AlertCard key={i} type={a.type}>
                                    <AlertIcon>{a.icon}</AlertIcon>
                                    <AlertContent>
                                        <AlertTitle type={a.type}>{a.title}</AlertTitle>
                                        <AlertDetail>{a.detail}</AlertDetail>
                                    </AlertContent>
                                </AlertCard>
                            );
                        })}
                    </AlertsContainer>
                ) : null}

                {/* Vital Signs */}
                <DetailSection>
                    <DetailSectionTitle>🩺 Vital Signs</DetailSectionTitle>
                    <DetailGrid>
                        <DetailItem>
                            <DetailItemLabel>Weight</DetailItemLabel>
                            <DetailItemValue>
                                {contact.weight_kg ? contact.weight_kg + " kg" : "—"}
                            </DetailItemValue>
                            {prevContact ? (function() {
                                const c = getCompare(contact.weight_kg, prevContact.weight_kg, "kg", false);
                                return c ? (
                                    <span style={{ fontSize: "0.68rem", color: c.color, fontWeight: 600 }}>
                                        {c.label}
                                    </span>
                                ) : null;
                            })() : null}
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>BMI</DetailItemLabel>
                            <DetailItemValue>
                                {contact.bmi || "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>MUAC</DetailItemLabel>
                            <DetailItemValue>
                                {contact.muac_cm ? contact.muac_cm + " cm" : "—"}
                                {contact.muac_color_code ? (
                                    <span
                                        style={{
                                            display: "inline-block",
                                            width: "10px",
                                            height: "10px",
                                            borderRadius: "50%",
                                            background:
                                                contact.muac_color_code === "green"
                                                    ? "#2e7d32"
                                                    : contact.muac_color_code === "yellow"
                                                    ? "#f57f17"
                                                    : "#c62828",
                                            marginLeft: "6px",
                                            boxShadow:
                                                "0 0 0 3px " +
                                                (contact.muac_color_code === "green"
                                                    ? "#2e7d32"
                                                    : contact.muac_color_code === "yellow"
                                                    ? "#f57f17"
                                                    : "#c62828") + "30",
                                        }}
                                    />
                                ) : null}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Blood Pressure</DetailItemLabel>
                            <DetailItemValue
                                danger={sys >= 140 || dia >= 90}
                            >
                                {contact.blood_pressure_systolic || "—"}/
                                {contact.blood_pressure_diastolic || "—"} mmHg
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Pulse Rate</DetailItemLabel>
                            <DetailItemValue>
                                {contact.pulse_rate ? contact.pulse_rate + " bpm" : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Temperature</DetailItemLabel>
                            <DetailItemValue
                                danger={temp >= 37.5}
                            >
                                {contact.temperature ? contact.temperature + " °C" : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Respiratory Rate</DetailItemLabel>
                            <DetailItemValue>
                                {contact.respiratory_rate
                                    ? contact.respiratory_rate + " /min"
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Gestational Age</DetailItemLabel>
                            <DetailItemValue>
                                {contact.gestational_age_weeks
                                    ? contact.gestational_age_weeks + " weeks"
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                    </DetailGrid>
                </DetailSection>

                {/* Abdominal Examination */}
                <DetailSection>
                    <DetailSectionTitle>🤰 Abdominal Examination</DetailSectionTitle>
                    <DetailGrid>
                        <DetailItem>
                            <DetailItemLabel>Fundal Height</DetailItemLabel>
                            <DetailItemValue>
                                {contact.fundal_height_cm
                                    ? contact.fundal_height_cm + " cm"
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Fetal Heart Rate</DetailItemLabel>
                            <DetailItemValue
                                danger={fhr && (fhr < 110 || fhr > 160)}
                            >
                                {contact.fetal_heart_rate
                                    ? contact.fetal_heart_rate + " bpm"
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Presentation</DetailItemLabel>
                            <DetailItemValue>
                                {contact.fetal_presentation
                                    ? contact.fetal_presentation.charAt(0).toUpperCase() +
                                      contact.fetal_presentation.slice(1)
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Position</DetailItemLabel>
                            <DetailItemValue>
                                {contact.fetal_position || "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Presenting Part (Fifths)</DetailItemLabel>
                            <DetailItemValue>
                                {contact.presenting_part_to_brim || "—"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Oedema</DetailItemLabel>
                            <DetailItemValue
                                danger={contact.oedema === "severe"}
                            >
                                {contact.oedema
                                    ? contact.oedema.charAt(0).toUpperCase() +
                                      contact.oedema.slice(1)
                                    : "None"}
                            </DetailItemValue>
                        </DetailItem>
                    </DetailGrid>
                </DetailSection>

                {/* Urinalysis & Lab */}
                <DetailSection>
                    <DetailSectionTitle>🧪 Urinalysis &amp; Haemoglobin</DetailSectionTitle>
                    <DetailGrid>
                        <DetailItem>
                            <DetailItemLabel>Urine Protein</DetailItemLabel>
                            <DetailItemValue
                                danger={
                                    contact.urine_protein === "++" ||
                                    contact.urine_protein === "+++"
                                }
                            >
                                {contact.urine_protein || "Nil"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Urine Glucose</DetailItemLabel>
                            <DetailItemValue>
                                {contact.urine_glucose || "Nil"}
                            </DetailItemValue>
                        </DetailItem>
                        <DetailItem>
                            <DetailItemLabel>Haemoglobin</DetailItemLabel>
                            <DetailItemValue
                                danger={hb && hb < 11}
                            >
                                {contact.haemoglobin
                                    ? contact.haemoglobin + " g/dL"
                                    : "—"}
                            </DetailItemValue>
                        </DetailItem>
                    </DetailGrid>
                </DetailSection>

                {/* Clinical Notes */}
                {(contact.complaints || contact.clinical_notes) ? (
                    <DetailSection>
                        <DetailSectionTitle>📝 Clinical Notes</DetailSectionTitle>
                        {contact.complaints ? (
                            <ClinicalNotesBox style={{ marginBottom: "12px" }}>
                                <ClinicalNotesLabel>Presenting Complaints</ClinicalNotesLabel>
                                <ClinicalNotesText>{contact.complaints}</ClinicalNotesText>
                            </ClinicalNotesBox>
                        ) : null}
                        {contact.clinical_notes ? (
                            <ClinicalNotesBox>
                                <ClinicalNotesLabel>Clinical Notes &amp; Plan</ClinicalNotesLabel>
                                <ClinicalNotesText>{contact.clinical_notes}</ClinicalNotesText>
                            </ClinicalNotesBox>
                        ) : null}
                    </DetailSection>
                ) : null}

                {/* Next appointment */}
                {contact.next_appointment_date ? (
                    <DetailSection>
                        <DetailSectionTitle>📅 Next Appointment</DetailSectionTitle>
                        <div
                            style={{
                                padding: "10px 16px",
                                background: "#ebf0f9",
                                borderRadius: "8px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <span style={{ fontSize: "0.88rem", fontWeight: 600, color: "#3871B7" }}>
                                {new Date(contact.next_appointment_date).toLocaleDateString("en-GB", {
                                    weekday: "long",
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </span>
                        </div>
                    </DetailSection>
                ) : null}

                {/* Seen by */}
                {contact.seen_by ? (
                    <SeenByBadge>
                        👩‍⚕️ Seen by: {contact.seen_by}
                    </SeenByBadge>
                ) : null}
            </DetailBody>
        </DetailCard>
    );
};

export default ANCContactTimeline;
